/**
 * Evaluation workspace — the console a procurement lead lands on mid-tender.
 *
 * This was previously a marketing page: a value proposition, feature cards and
 * a capability grid, shown to someone already signed into the product. It has
 * been rebuilt around the one question that matters on arrival — where does the
 * live evaluation stand, and what needs my decision — because public
 * procurement runs on a statutory clock with legal consequence attached.
 *
 * Everything on this page is derived from the same fixtures and scoring engine
 * as the two flows, so the console and the workspace never disagree.
 */
import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import {
  Scale, Users, ArrowRight, AlertTriangle, CheckCircle2, Gavel,
  FileText, ShieldCheck, Sparkles, Clock,
} from 'lucide-react';
import { theme } from '../styles/theme';
import {
  Shell, Eyebrow, PageTitle, PageSub, HeaderRow, Panel, PanelHead, PanelTitle,
  PanelNote, PanelBody, Split, MicroLabel, Mono, Tag, Num, Pulse, Actions,
} from '../components/evaluation/EvalKit';
import {
  tender, bidders, anomalies, auditTrail, riskFactors, scoringAgents,
} from '../data/evaluationMocks';
import {
  rankAll, initialBands, divergences, allSubCriteria, excludedBidders,
} from '../utils/evaluationEngine';

/* Fixed "today" for the demo cycle — the evaluation window is mid-flight. */
const TODAY = new Date('2026-08-25T00:00:00Z');

const workingDaysBetween = (from: Date, to: Date) => {
  let d = new Date(from), n = 0;
  while (d < to) {
    d.setUTCDate(d.getUTCDate() + 1);
    const dow = d.getUTCDay();
    if (dow !== 0 && dow !== 6) n++;
  }
  return n;
};

const day = (iso: string) => new Date(iso + 'T00:00:00Z');

/** "17–28 Aug" for a range, "14 Aug" for a single date. */
const fmt = (raw: string) => {
  if (!raw.includes('→')) {
    return day(raw.trim()).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  const [a, b] = raw.split('→').map(x => day(x.trim()));
  const sameMonth = a.getUTCMonth() === b.getUTCMonth();
  const aStr = sameMonth
    ? String(a.getUTCDate())
    : a.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${aStr}–${b.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
};

/* ------------------------------------------------------------ signature -- */

const drawLine = keyframes`from { transform: scaleX(0) } to { transform: scaleX(1) }`;

const ClockCard = styled.section`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 18px;
  overflow: hidden;
  margin-bottom: ${theme.spacing[5]};
`;

const ClockHead = styled.div`
  display: flex; align-items: flex-start; gap: ${theme.spacing[5]};
  padding: ${theme.spacing[5]} ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.borderLight};
  flex-wrap: wrap;
`;

const Ref = styled.div`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 12px;
  letter-spacing: 0.04em;
  color: ${theme.colors.primary[500]};
  background: ${theme.colors.primary[50]};
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: 7px;
  padding: 3px 9px;
  display: inline-block;
  margin-bottom: 8px;
`;

const ClockTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${theme.colors.textPrimary};
  margin: 0 0 5px;
`;

const ClockMeta = styled.div`
  font-size: 12.5px;
  color: ${theme.colors.textMuted};
  display: flex; gap: ${theme.spacing[2]}; flex-wrap: wrap; align-items: center;
`;

const Countdown = styled.div`
  margin-left: auto;
  text-align: right;
  flex: 0 0 auto;
`;

const CountNum = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.03em;
  color: #B45309;
  font-variant-numeric: tabular-nums;
`;

const CountLabel = styled.div`
  font-size: 10.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  font-weight: 700;
  margin-top: 4px;
`;

/* The rail itself: a statutory track with today marked between two stages. */
const Rail = styled.div`
  position: relative;
  padding: ${theme.spacing[8]} ${theme.spacing[6]} ${theme.spacing[6]};
`;

const Track = styled.div`
  position: absolute;
  left: ${theme.spacing[6]}; right: ${theme.spacing[6]};
  top: calc(${theme.spacing[8]} + 5px);
  height: 2px;
  background: ${theme.colors.borderLight};
`;

const TrackDone = styled.div<{ $pct: number }>`
  position: absolute; inset: 0 auto 0 0;
  width: ${p => p.$pct}%;
  background: ${theme.colors.primary[500]};
  transform-origin: left;
  animation: ${drawLine} 900ms cubic-bezier(.4,0,.2,1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Stops = styled.ol<{ $count: number }>`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${p => p.$count}, 1fr);
  list-style: none;
  margin: 0; padding: 0;
  gap: ${theme.spacing[2]};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[4]};
  }
`;

const Stop = styled.li<{ $state: 'done' | 'now' | 'todo' }>`
  position: relative;
  padding-top: 22px;
  min-width: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: ${p => (p.$state === 'now' ? '12px' : '10px')};
    height: ${p => (p.$state === 'now' ? '12px' : '10px')};
    border-radius: 50%;
    background: ${p =>
      p.$state === 'done' ? theme.colors.primary[500]
      : p.$state === 'now' ? '#B45309'
      : theme.colors.surface};
    border: 2px solid ${p =>
      p.$state === 'done' ? theme.colors.primary[500]
      : p.$state === 'now' ? '#B45309'
      : theme.colors.border};
    box-shadow: ${p => (p.$state === 'now' ? '0 0 0 5px rgba(180,83,9,.14)' : 'none')};
  }

  @media (max-width: 900px) {
    padding-top: 0; padding-left: 22px;
    &::before { top: 3px; }
  }
`;

const StopLabel = styled.div<{ $state: 'done' | 'now' | 'todo' }>`
  font-size: 12px;
  font-weight: ${p => (p.$state === 'now' ? 700 : 600)};
  color: ${p =>
    p.$state === 'now' ? '#B45309'
    : p.$state === 'todo' ? theme.colors.textDisabled
    : theme.colors.textPrimary};
  line-height: 1.35;
  margin-bottom: 3px;
  min-height: 2.7em;

  @media (max-width: 900px) { min-height: 0; }
`;

const StopDate = styled.div`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 11px;
  color: ${theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const StopNote = styled.div`
  font-size: 10.5px;
  color: ${theme.colors.textDisabled};
  margin-top: 2px;
`;

/* ------------------------------------------------------------- console --- */

const Figures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  border-top: 1px solid ${theme.colors.borderLight};
`;

const Figure = styled.div`
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border-right: 1px solid ${theme.colors.borderLight};
  &:last-child { border-right: 0; }
`;

const FigureVal = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 1.45rem;
  font-weight: 700;
  color: ${theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

const TaskRow = styled(Link)`
  display: flex; align-items: flex-start; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.borderLight};
  text-decoration: none;
  transition: background 140ms ease;
  &:last-child { border-bottom: 0; }
  &:hover { background: ${theme.colors.backgroundSecondary}; }
  &:focus-visible { outline: 2px solid ${theme.colors.primary[400]}; outline-offset: -2px; }
`;

const TaskIcon = styled.span<{ $tone: 'signal' | 'ink' }>`
  flex: 0 0 auto; margin-top: 1px;
  color: ${p => (p.$tone === 'signal' ? '#B45309' : theme.colors.primary[500])};
`;

const TaskText = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.textPrimary};
  line-height: 1.45;
`;

const TaskMeta = styled.div`
  font-size: 11.5px;
  color: ${theme.colors.textMuted};
  margin-top: 3px;
  line-height: 1.5;
`;

const LogRow = styled.div`
  display: flex; align-items: baseline; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.borderLight};
  &:last-child { border-bottom: 0; }
`;

const LogTime = styled.span`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 10.5px;
  color: ${theme.colors.textDisabled};
  flex: 0 0 92px;
  font-variant-numeric: tabular-nums;
`;

const LogText = styled.span`
  font-size: 12.5px;
  color: ${theme.colors.textSecondary};
  line-height: 1.5;
  strong { color: ${theme.colors.textPrimary}; font-weight: 600; }
`;

/* Entry points to the two flows. */
const EntryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[5]};
  margin-bottom: ${theme.spacing[5]};
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Entry = styled(Link)`
  display: block;
  padding: ${theme.spacing[6]};
  border: 1px solid ${theme.colors.border};
  border-radius: 16px;
  background: ${theme.colors.surface};
  text-decoration: none;
  transition: border-color 160ms ease, transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    border-color: ${theme.colors.primary[300]};
    transform: translateY(-2px);
    box-shadow: 0 14px 34px -24px rgba(0,0,82,.45);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover { transform: none; }
  }
  &:focus-visible { outline: 2px solid ${theme.colors.primary[400]}; outline-offset: 2px; }
`;

const EntryIcon = styled.span`
  width: 38px; height: 38px; border-radius: 10px;
  display: grid; place-items: center;
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[4]};
`;

const EntryTitle = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 16px; font-weight: 700;
  color: ${theme.colors.textPrimary};
  margin-bottom: 5px;
  display: flex; align-items: center; gap: 8px;
`;

const StepNo = styled.span`
  width: 20px; height: 20px; border-radius: 6px;
  display: inline-grid; place-items: center;
  background: ${theme.colors.primary[500]};
  color: #fff;
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 11px; font-weight: 700;
  flex: 0 0 auto;
`;

const EntryBody = styled.p`
  font-size: 13px; line-height: 1.6;
  color: ${theme.colors.textMuted};
  margin: 0 0 ${theme.spacing[4]};
`;

const EntrySteps = styled.div`
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 10.5px;
  color: ${theme.colors.textDisabled};
  letter-spacing: 0.02em;
`;

/* Capability strip — what the agents do, stated once, without card theatre. */
const CapStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: ${theme.spacing[5]};
  padding: ${theme.spacing[5]} ${theme.spacing[6]};
`;

const Cap = styled.div`
  border-left: 2px solid ${theme.colors.primary[200]};
  padding-left: ${theme.spacing[4]};
`;

const CapName = styled.div`
  font-size: 12.5px; font-weight: 700;
  color: ${theme.colors.textPrimary};
  margin-bottom: 3px;
`;

const CapBody = styled.div`
  font-size: 11.5px; line-height: 1.55;
  color: ${theme.colors.textMuted};
`;

/* The sample is fenced off so nobody mistakes a worked example for their own
   live procurement — the mistake this page made in its first cut. */
const SampleFrame = styled.section`
  border: 1px solid ${theme.colors.border};
  border-radius: 18px;
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[5]};
  margin-bottom: ${theme.spacing[5]};
`;

const SampleLabel = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing[4]};

  .rule { flex: 1; height: 1px; background: ${theme.colors.border}; }
  .plain {
    letter-spacing: 0; text-transform: none; font-weight: 500; font-size: 12px;
    color: ${theme.colors.textDisabled};
  }
`;

const SampleFoot = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding-top: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const SampleLink = styled(Link)`
  display: inline-flex; align-items: center; gap: 8px;
  background: ${theme.colors.primary[500]};
  color: #fff;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 13px; font-weight: 600;
  text-decoration: none;
  transition: background 150ms ease;

  /* colour restated on every state: the global a:hover rule outranks the
     base class colour and would render the label navy-on-navy. */
  &:hover { background: ${theme.colors.primary[600]}; color: #fff; }
  &:visited, &:active, &:focus { color: #fff; }
  &:focus-visible { outline: 2px solid ${theme.colors.primary[400]}; outline-offset: 2px; }
`;

const SampleNote = styled.span`
  font-size: 12px;
  color: ${theme.colors.textMuted};
`;

/* -------------------------------------------------------------- page ----- */

const EvaluationPage: React.FC = () => {
  const bands = useMemo(() => initialBands(), []);
  const ranked = useMemo(() => rankAll(bands), [bands]);
  const live = ranked.filter(r => !r.disqualified);
  const divs = useMemo(() => divergences(bands), [bands]);
  const excluded = excludedBidders();

  const awardNotice = new Date('2026-09-02T00:00:00Z');
  const daysToNotice = workingDaysBetween(TODAY, awardNotice);

  const stages = tender.timeline;
  const activeIdx = stages.findIndex(s => (s as any).active);
  const donePct = ((activeIdx) / (stages.length - 1)) * 100;

  const openFlags = anomalies.filter(a => a.status !== 'resolved');

  const tasks = [
    {
      to: '/evaluation/response-scoring',
      tone: 'signal' as const,
      icon: <Gavel size={16} />,
      text: `Moderate ${divs.length} scoring divergences`,
      meta: `Two assessors disagreed on ${divs.length} of ${live.length * allSubCriteria.length} assessments. One exceeds a single band and needs a joint rationale recorded.`,
    },
    {
      to: '/evaluation/response-scoring',
      tone: 'signal' as const,
      icon: <CheckCircle2 size={16} />,
      text: 'Approve the award recommendation',
      meta: `${live[0]?.name} leads at ${live[0]?.total.toFixed(2)} of 100, ${(live[0]?.total - (live[1]?.total ?? 0)).toFixed(2)} ahead. Approval releases the assessment summaries.`,
    },
    {
      to: '/evaluation/response-scoring',
      tone: 'ink' as const,
      icon: <ShieldCheck size={16} />,
      text: `Confirm ${excluded.length} exclusions before the award notice`,
      meta: `${excluded.map(e => e.shortName).join(' and ')} failed mandatory requirements. Exclusions must be reasoned in each assessment summary.`,
    },
    {
      to: '/evaluation/issuer-flow',
      tone: 'ink' as const,
      icon: <FileText size={16} />,
      text: 'Review contract risks carried into award',
      meta: `${riskFactors.length} risks logged against tendered positions. Mitigations become contract conditions at signature.`,
    },
  ];

  const capabilities = [
    { name: 'Criteria extraction', body: 'Award criteria, sub-criteria and weightings resolved from the tender pack, each traced to a clause and page.' },
    { name: 'Compliance gate', body: 'Mandatory requirements and exclusion grounds tested before any quality assessment is undertaken.' },
    { name: 'Evidence assessment', body: 'Every band supported by a verbatim quote from the submission, with a confidence score attached.' },
    { name: 'Integrity checks', body: 'Cross-submission duplication, unevidenced claims and price anomalies surfaced for the panel.' },
  ];

  return (
    <Shell>
      <HeaderRow>
        <div>
          <Eyebrow>Evaluation</Eyebrow>
          <PageTitle>Evaluation workspace</PageTitle>
          <PageSub>
            Score submitted tenders against a published evaluation model and produce an
            award recommendation you can defend — every score anchored to a quote, a clause
            and a page in the supplier&rsquo;s own submission.
          </PageSub>
        </div>
        <Actions>
          <Tag $tone="muted"><ShieldCheck size={11} /> {tender.regime}</Tag>
          <SampleLink to="/evaluation/issuer-flow">
            <Sparkles size={15} /> New evaluation
          </SampleLink>
        </Actions>
      </HeaderRow>

      {/* ------------------------------------------------- start here ----- */}
      <EntryGrid>
        <Entry to="/evaluation/issuer-flow" aria-label="Open the issuer flow">
          <EntryIcon><Users size={19} /></EntryIcon>
          <EntryTitle><StepNo>1</StepNo> Issuer flow <ArrowRight size={15} /></EntryTitle>
          <EntryBody>
            You are running the procurement. Turn a tender pack into a published evaluation
            model — criteria, sub-criteria, weightings and scoring methodology — validated
            against the regime before it goes to market.
          </EntryBody>
          <EntrySteps>Tender package → Extraction → Weighting → Publish</EntrySteps>
        </Entry>

        <Entry to="/evaluation/response-scoring" aria-label="Open response scoring">
          <EntryIcon><Scale size={19} /></EntryIcon>
          <EntryTitle><StepNo>2</StepNo> Response scoring <ArrowRight size={15} /></EntryTitle>
          <EntryBody>
            The tenders are in. Assess them against the published model, moderate to a panel
            consensus, and generate an assessment summary for every supplier ahead of the
            standstill period.
          </EntryBody>
          <EntrySteps>Submissions → Compliance → Scoring → Moderation → Award</EntrySteps>
        </Entry>
      </EntryGrid>

      {/* ------------------------------------------- worked example ------- */}
      <SampleFrame>
        <SampleLabel>
          <FileText size={12} />
          Evaluation in progress
          <span className="rule" />
        </SampleLabel>

        <ClockCard style={{ marginBottom: theme.spacing[5] }}>
          <ClockHead>
            <div style={{ minWidth: 0 }}>
              <Ref>{tender.reference}</Ref>
              <ClockTitle>{tender.title}</ClockTitle>
              <ClockMeta>
                <span>{tender.buyer}</span>
                <span aria-hidden>·</span>
                <span>{tender.procedure}</span>
                <span aria-hidden>·</span>
                <span>{tender.awardBasis}</span>
                <span aria-hidden>·</span>
                <Mono>{tender.ftsNotice}</Mono>
              </ClockMeta>
            </div>
            <Countdown>
              <CountNum>{daysToNotice}</CountNum>
              <CountLabel>Working days to award notice</CountLabel>
            </Countdown>
          </ClockHead>

          <Rail>
            <Track><TrackDone $pct={donePct} /></Track>
            <Stops $count={stages.length}>
              {stages.map((s) => {
                const state = s.done ? 'done' : (s as any).active ? 'now' : 'todo';
                return (
                  <Stop key={s.label} $state={state}>
                    <StopLabel $state={state}>{s.label}</StopLabel>
                    <StopDate>{fmt(s.date)}</StopDate>
                    {s.note && <StopNote>{s.note}</StopNote>}
                  </Stop>
                );
              })}
            </Stops>
          </Rail>

          <Figures>
            <Figure><MicroLabel>Tenders</MicroLabel><FigureVal>{bidders.length}</FigureVal></Figure>
            <Figure><MicroLabel>Assessed</MicroLabel><FigureVal>{live.length}</FigureVal></Figure>
            <Figure><MicroLabel>Excluded</MicroLabel><FigureVal>{excluded.length}</FigureVal></Figure>
            <Figure><MicroLabel>Assessments</MicroLabel><FigureVal>{live.length * allSubCriteria.length}</FigureVal></Figure>
            <Figure><MicroLabel>Divergences</MicroLabel><FigureVal>{divs.length}</FigureVal></Figure>
            <Figure><MicroLabel>Open flags</MicroLabel><FigureVal>{openFlags.length}</FigureVal></Figure>
          </Figures>
        </ClockCard>

        <Split $ratio="1.15fr 1fr">
          <Panel style={{ marginBottom: 0 }}>
            <PanelHead>
              <div>
                <PanelTitle><AlertTriangle size={15} /> Open decisions</PanelTitle>
                <PanelNote>{tasks.length} items the panel must settle before the award notice.</PanelNote>
              </div>
            </PanelHead>
            <PanelBody $flush>
              {tasks.map(t => (
                <TaskRow key={t.text} to={t.to}>
                  <TaskIcon $tone={t.tone}>{t.icon}</TaskIcon>
                  <div>
                    <TaskText>{t.text}</TaskText>
                    <TaskMeta>{t.meta}</TaskMeta>
                  </div>
                  <ArrowRight size={15} color={theme.colors.textDisabled} style={{ marginLeft: 'auto', flex: '0 0 auto', alignSelf: 'center' }} />
                </TaskRow>
              ))}
            </PanelBody>
          </Panel>

          <Panel style={{ marginBottom: 0 }}>
            <PanelHead>
              <div>
                <PanelTitle><Sparkles size={15} /> Agent activity</PanelTitle>
                <PanelNote>Last run 25 Aug, {scoringAgents.length} agents.</PanelNote>
              </div>
            </PanelHead>
            <PanelBody $flush>
              {[...auditTrail]
                .sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
                .slice(0, 7)
                .map((e, i) => (
                  <LogRow key={i}>
                    <LogTime>{new Date(e.ts).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</LogTime>
                    <LogText><strong>{e.event}</strong> — {e.detail}</LogText>
                  </LogRow>
              ))}
            </PanelBody>
          </Panel>
        </Split>

        <SampleFoot>
          <SampleLink to="/evaluation/response-scoring">
            Continue evaluation <ArrowRight size={15} />
          </SampleLink>
          <SampleNote>
            {tender.reference} — {bidders.length} tenders, from compliance through moderation
            to the assessment summaries.
          </SampleNote>
        </SampleFoot>
      </SampleFrame>

      {/* ---------------------------------------------------- capabilities */}
      <Panel>
        <PanelHead>
          <div>
            <PanelTitle><ShieldCheck size={15} /> What the agents do</PanelTitle>
            <PanelNote>Every output is page-anchored to the source document.</PanelNote>
          </div>
          <Tag $tone="muted"><Clock size={11} /> Audit trail retained in full</Tag>
        </PanelHead>
        <CapStrip>
          {capabilities.map(c => (
            <Cap key={c.name}>
              <CapName>{c.name}</CapName>
              <CapBody>{c.body}</CapBody>
            </Cap>
          ))}
        </CapStrip>
      </Panel>
    </Shell>
  );
};

export default EvaluationPage;
